import React, { useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    alpha,
    Button,
} from '@mui/material';
import { darken } from '@mui/material/styles';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { SEARCE_COLORS } from '../../theme';
import MonthRangePicker from '../MonthRangePicker';

interface MonthlyCertificationsData {
    month: string;
    year?: number;
    count: number;
    monthAbbr?: string;
    sortKey?: string;
}

interface CertificationsTrendChartProps {
    data: MonthlyCertificationsData[];
    startMonth?: { year: number; month: number } | null;
    endMonth?: { year: number; month: number } | null;
    onMonthRangeChange?: (start: { year: number; month: number } | null, end: { year: number; month: number } | null) => void;
    title?: string;
    lineName?: string;
    // Toggle button props
    chartType?: 'new' | 'active' | 'expiring';
    onChartTypeChange?: (type: 'new' | 'active' | 'expiring') => void;
    newCertificationsData?: MonthlyCertificationsData[];
    activeCertificationsData?: MonthlyCertificationsData[];
    expiringCertificationsData?: MonthlyCertificationsData[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                backgroundColor: SEARCE_COLORS.cardBackground,
                border: `1px solid ${SEARCE_COLORS.border}`,
                borderRadius: 2,
                p: 1.5,
                boxShadow: `0 4px 12px ${alpha(SEARCE_COLORS.primary, 0.15)}`,
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: SEARCE_COLORS.textPrimary }}>
                {label}
            </Typography>
            {payload.map((entry: any, index: number) => (
                <Typography
                    key={index}
                    variant="body2"
                    sx={{
                        color: entry.color,
                        fontWeight: 500,
                        mb: 0.5,
                    }}
                >
                    {`${entry.name}: ${entry.value.toLocaleString()}`}
                </Typography>
            ))}
        </Box>
    );
};

const CertificationsTrendChart: React.FC<CertificationsTrendChartProps> = ({
    data,
    startMonth,
    endMonth,
    onMonthRangeChange,
    title = "New Certifications Trend",
    lineName = "Certifications",
    chartType,
    onChartTypeChange,
    newCertificationsData,
    activeCertificationsData,
    expiringCertificationsData,
}) => {
    const showFilter = startMonth !== undefined && endMonth !== undefined && onMonthRangeChange !== undefined;
    const showToggle = chartType !== undefined && onChartTypeChange !== undefined && newCertificationsData !== undefined && activeCertificationsData !== undefined && expiringCertificationsData !== undefined;
    const currentData = showToggle ? (
        chartType === 'new' ? newCertificationsData : 
        chartType === 'active' ? activeCertificationsData : 
        expiringCertificationsData
    ) : data;
    const currentLineName = showToggle ? (
        chartType === 'new' ? 'New Certifications' : 
        chartType === 'active' ? 'Active Certifications' : 
        'Expiring Certifications'
    ) : lineName;

    // Debug logging
    useEffect(() => {
        // console.log('CertificationsTrendChart render:', {
        //     dataLength: data?.length || 0,
        //     hasData: data && data.length > 0,
        //     startMonth,
        //     endMonth,
        //     showFilter,
        //     firstDataPoint: data?.[0]
        // });
    }, [data, startMonth, endMonth, showFilter]);

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                background: `linear-gradient(135deg, ${SEARCE_COLORS.cardBackground} 0%, ${alpha(SEARCE_COLORS.primary, 0.02)} 100%)`,
                border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                },
            }}
        >
            <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', width: '100%', position: 'relative', overflow: 'hidden', minHeight: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, mb: 2 }}>
                    Certifications Trend
                </Typography>
                {(showFilter || showToggle) && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1.5,
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {showToggle && (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Button
                                        variant={chartType === 'new' ? 'contained' : 'outlined'}
                                        onClick={() => onChartTypeChange!('new')}
                                        size="small"
                                        sx={{
                                            width:20,
                                            fontSize:10,
                                            backgroundColor: chartType === 'new' ? SEARCE_COLORS.primary : 'transparent',
                                            color: chartType === 'new' ? SEARCE_COLORS.white : SEARCE_COLORS.textPrimary,
                                            borderColor: SEARCE_COLORS.primary,
                                            '&:hover': {
                                                backgroundColor: chartType === 'new' ? darken(SEARCE_COLORS.primary, 0.1) : alpha(SEARCE_COLORS.primary, 0.1),
                                                borderColor: SEARCE_COLORS.primary,
                                            },
                                        }}
                                    >
                                        New
                                    </Button>
                                    <Button
                                        variant={chartType === 'active' ? 'contained' : 'outlined'}
                                        onClick={() => onChartTypeChange!('active')}
                                        size="small"
                                        sx={{
                                            width:20,
                                            fontSize:10,
                                            backgroundColor: chartType === 'active' ? SEARCE_COLORS.primary : 'transparent',
                                            color: chartType === 'active' ? SEARCE_COLORS.white : SEARCE_COLORS.textPrimary,
                                            borderColor: SEARCE_COLORS.primary,
                                            '&:hover': {
                                                backgroundColor: chartType === 'active' ? darken(SEARCE_COLORS.primary, 0.1) : alpha(SEARCE_COLORS.primary, 0.1),
                                                borderColor: SEARCE_COLORS.primary,
                                            },
                                        }}
                                    >
                                        Active
                                    </Button>
                                    <Button
                                        variant={chartType === 'expiring' ? 'contained' : 'outlined'}
                                        onClick={() => onChartTypeChange!('expiring')}
                                        size="small"
                                        sx={{
                                            width:20,
                                            fontSize:10,
                                            backgroundColor: chartType === 'expiring' ? SEARCE_COLORS.warning : 'transparent',
                                            color: chartType === 'expiring' ? SEARCE_COLORS.white : SEARCE_COLORS.textPrimary,
                                            borderColor: SEARCE_COLORS.warning,
                                            '&:hover': {
                                                backgroundColor: chartType === 'expiring' ? darken(SEARCE_COLORS.warning, 0.1) : alpha(SEARCE_COLORS.warning, 0.1),
                                                borderColor: SEARCE_COLORS.warning,
                                            },
                                        }}
                                    >
                                        Expiring
                                    </Button>
                                </Box>
                            )}
                            {title && (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        color: SEARCE_COLORS.textPrimary,
                                        fontSize: '1rem',
                                    }}
                                >
                                    {title}
                                </Typography>
                            )}
                        </Box>
                        {showFilter && (
                            <Box sx={{ width: 180 }}>
                                <MonthRangePicker
                                    startMonth={startMonth || null}
                                    endMonth={endMonth || null}
                                    onChange={onMonthRangeChange || (() => { })}
                                    label="Duration"
                                />
                            </Box>
                        )}
                    </Box>
                )}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                    {!currentData || currentData.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                            <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
                                {showFilter && (!startMonth || !endMonth)
                                    ? 'Please select a date range to view certification trends'
                                    : showFilter
                                        ? 'No certification data available for the selected period'
                                        : 'No certification data available'}
                            </Typography>
                        </Box>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={currentData}
                                margin={{ top: 5, right: 15, left: 5, bottom: 10 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)} />
                                <XAxis
                                dataKey="monthAbbr"
                                tick={{ fontSize: 9, fill: SEARCE_COLORS.textSecondary }}
                                axisLine={{ stroke: SEARCE_COLORS.border }}
                                tickLine={false}
                                angle={currentData.some((item: any) => item.year) ? -45 : 0}
                                textAnchor={currentData.some((item: any) => item.year) ? 'end' : 'middle'}
                                height={currentData.some((item: any) => item.year) ? 50 : 25}
                            />
                                <YAxis
                                    tick={{ fontSize: 10, fill: SEARCE_COLORS.textSecondary }}
                                    axisLine={{ stroke: SEARCE_COLORS.border }}
                                    tickLine={false}
                                    width={40}
                                />
                                <RechartsTooltip
                                    content={<CustomTooltip />}
                                    allowEscapeViewBox={{ x: false, y: false }}
                                    wrapperStyle={{ zIndex: 9999, pointerEvents: 'none' }}
                                    contentStyle={{ zIndex: 9999, pointerEvents: 'none' }}
                                />
                                <Legend
                                    wrapperStyle={{
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        paddingTop: '3px',
                                    }}
                                    iconType="line"
                                    iconSize={10}
                                />
                                <Line
                                type="monotone"
                                dataKey="count"
                                stroke={chartType === 'expiring' ? SEARCE_COLORS.warning : SEARCE_COLORS.primary}
                                strokeWidth={2}
                                dot={{ fill: chartType === 'expiring' ? SEARCE_COLORS.warning : SEARCE_COLORS.primary, strokeWidth: 1.5, r: 3 }}
                                activeDot={{ r: 5, stroke: chartType === 'expiring' ? SEARCE_COLORS.warning : SEARCE_COLORS.primary, strokeWidth: 2 }}
                                name={currentLineName}
                            />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default CertificationsTrendChart;
